/**
 * BrandSignature — the timeless mark of THE KC INDIA.
 *
 *   THE KC INDIA
 *   KC — Kedarnath Celebrations
 *   Celebrate Every Moment in the Lap of the Himalayas.
 *
 * Variants:
 *   size = "sm" | "md" | "lg"
 *   tone = "ink" (dark on light) | "ivory" (light on dark)
 *   showWordmark = boolean — whether to render "THE KC INDIA" above
 *   align = "left" | "center"
 *
 * Fade-in on mount (CSS animation) — subtle, editorial, spiritual.
 */

const OrnamentDot = ({ tone }) => (
  <span className={`inline-flex items-center gap-2 ${tone === "ivory" ? "text-[#D4AF37]" : "text-gold"}`} aria-hidden="true">
    <span className={`h-px w-8 ${tone === "ivory" ? "bg-[#D4AF37]/50" : "bg-gold/50"}`} />
    <span className={`w-1 h-1 rounded-full ${tone === "ivory" ? "bg-[#D4AF37]" : "bg-gold"}`} />
    <span className={`h-px w-8 ${tone === "ivory" ? "bg-[#D4AF37]/50" : "bg-gold/50"}`} />
  </span>
);

const size = {
  sm: {
    wordmark: "text-[13px] tracking-[0.42em]",
    line1:    "text-[15px] md:text-[17px]",
    line2:    "text-[10px] md:text-[11px] tracking-[0.28em]",
    gap:      "mt-3",
    gap2:     "mt-2",
  },
  md: {
    wordmark: "text-[14px] tracking-[0.44em]",
    line1:    "text-[19px] md:text-[22px]",
    line2:    "text-[11px] md:text-[12px] tracking-[0.32em]",
    gap:      "mt-4",
    gap2:     "mt-3",
  },
  lg: {
    wordmark: "text-[15px] md:text-[16px] tracking-[0.46em]",
    line1:    "text-[26px] md:text-[32px]",
    line2:    "text-[11px] md:text-[13px] tracking-[0.36em]",
    gap:      "mt-6",
    gap2:     "mt-4",
  },
};

export const BrandSignature = ({
  size: s = "md",
  tone = "ink",
  showWordmark = false,
  align = "left",
  delay = 0,
  className = "",
  "data-testid": testId = "brand-signature",
}) => {
  const cfg = size[s] || size.md;
  const wordmarkColor = tone === "ivory" ? "text-[#F8F6F2]" : "text-ink";
  const italicColor   = tone === "ivory" ? "text-[#D4AF37]" : "text-gold";
  const tagColor      = tone === "ivory" ? "text-[#F8F6F2]/70" : "text-ink-mute";
  const alignCls      = align === "center" ? "items-center text-center" : "items-start text-left";
  const shadow        = tone === "ivory" ? { textShadow: "0 2px 16px rgba(0,0,0,0.55)" } : undefined;

  return (
    <div
      className={`inline-flex flex-col ${alignCls} kc-fade-up ${className}`}
      style={{ animationDelay: `${delay}ms` }}
      data-testid={testId}
    >
      {showWordmark && (
        <span className={`uppercase font-normal ${cfg.wordmark} ${wordmarkColor}`} style={shadow}>
          THE KC INDIA
        </span>
      )}

      <OrnamentDot tone={tone} />

      <p className={`font-serif-italic ${cfg.line1} ${italicColor} ${cfg.gap} leading-tight tracking-[-0.005em]`} style={shadow}>
        KC — Kedarnath Celebrations
      </p>

      <p className={`font-serif-italic ${cfg.line1} ${tone === "ivory" ? "text-[#F8F6F2]" : "text-ink"} ${cfg.gap2} leading-tight tracking-[-0.005em]`} style={shadow}>
        Celebrate Every Moment in the Lap of the Himalayas.
      </p>

      <p className={`${cfg.line2} ${tagColor} uppercase ${cfg.gap2} font-normal`} style={shadow}>
        Nature · Comfort · Divinity
      </p>
    </div>
  );
};
